import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Trash2, Eye, Loader2, CheckCircle, Clock, XCircle, Filter, Search, User, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { value: 'financials', label: 'Financial Statements' },
    { value: 'tax', label: 'Tax Documents' },
    { value: 'receipts', label: 'Receipts & Invoices' },
    { value: 'bas', label: 'BAS Documents' },
    { value: 'payroll', label: 'Payroll Records' },
    { value: 'other', label: 'Other' }
];

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
    { value: 'reviewed', label: 'Reviewed', color: 'bg-blue-100 text-blue-700' },
    { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' }
];

const STATUS_ICONS = {
    pending: Clock,
    reviewed: Eye,
    approved: CheckCircle,
    rejected: XCircle
};

const AdminDocumentsPage = () => {
    const [documents, setDocuments] = useState([]);
    const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0, approved: 0 });
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [showDetailDialog, setShowDetailDialog] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('finmar_admin_token');
            let url = `${API}/admin/documents?limit=100`;
            if (filterStatus !== 'all') url += `&status=${filterStatus}`;
            if (filterCategory !== 'all') url += `&category=${filterCategory}`;

            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setDocuments(data.documents || []);
                setStats(data.stats || { total: 0, pending: 0, reviewed: 0, approved: 0 });
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterCategory]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleDownload = async (doc) => {
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/documents/${doc.document_id}/download`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = doc.filename;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            toast.error('Failed to download document');
        }
    };

    const handleStatusChange = async (docId, newStatus) => {
        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/documents/${docId}/status?status=${newStatus}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success(`Document marked as ${newStatus}`);
                fetchDocuments();
                setShowDetailDialog(false);
            } else {
                throw new Error('Failed to update status');
            }
        } catch (error) {
            toast.error('Failed to update document status');
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;

        try {
            const token = localStorage.getItem('finmar_admin_token');
            const response = await fetch(`${API}/admin/documents/${doc.document_id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Document deleted');
                fetchDocuments();
            } else {
                throw new Error('Failed to delete');
            }
        } catch (error) {
            toast.error('Failed to delete document');
        }
    };

    const openDocumentDetail = (doc) => {
        setSelectedDoc(doc);
        setShowDetailDialog(true);
    };

    return (
        <div className="space-y-6" data-testid="admin-documents-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-finmar-navy">Documents</h1>
                    <p className="text-slate-500">Manage uploaded financial documents</p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-slate-100 rounded-lg">
                                <FileText className="w-6 h-6 text-finmar-navy" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{stats.total}</p>
                                <p className="text-sm text-slate-500">Total Documents</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-amber-100 rounded-lg">
                                <Clock className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{stats.pending}</p>
                                <p className="text-sm text-slate-500">Pending Review</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Eye className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{stats.reviewed}</p>
                                <p className="text-sm text-slate-500">Reviewed</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-finmar-navy">{stats.approved}</p>
                                <p className="text-sm text-slate-500">Approved</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-500">Filters:</span>
                        </div>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                {STATUS_OPTIONS.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {CATEGORIES.map(c => (
                                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Documents Table */}
            <Card>
                <CardHeader>
                    <CardTitle>All Documents</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-finmar-gold" />
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-12">
                            <FileText className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                            <p className="text-slate-500">No documents found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Document</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Category</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Uploaded</th>
                                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {documents.map(doc => {
                                        const statusOption = STATUS_OPTIONS.find(s => s.value === doc.status) || STATUS_OPTIONS[0];
                                        const StatusIcon = STATUS_ICONS[doc.status] || Clock;

                                        return (
                                            <tr key={doc.document_id} className="border-b border-slate-50 hover:bg-slate-50">
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-3">
                                                        <FileText className="w-8 h-8 text-finmar-navy" />
                                                        <div>
                                                            <p className="font-medium text-finmar-navy truncate max-w-[200px]">{doc.filename}</p>
                                                            <p className="text-xs text-slate-400">{(doc.file_size / 1024).toFixed(1)} KB</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 bg-finmar-navy/10 rounded-full flex items-center justify-center">
                                                            <User className="w-4 h-4 text-finmar-navy" />
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-finmar-navy">{doc.user_name}</p>
                                                            <p className="text-xs text-slate-400">{doc.business_name}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="text-sm capitalize">{doc.category}</span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusOption.color}`}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {statusOption.label}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-sm text-slate-500">
                                                    {new Date(doc.uploaded_at).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-1">
                                                        <Button variant="ghost" size="icon" onClick={() => openDocumentDetail(doc)}>
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}>
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)} className="text-red-500 hover:text-red-700">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Document Detail Dialog */}
            <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-finmar-gold" />
                            Document Details
                        </DialogTitle>
                        <DialogDescription>
                            Review and manage this document
                        </DialogDescription>
                    </DialogHeader>

                    {selectedDoc && (
                        <div className="space-y-4 py-4">
                            <div className="bg-slate-50 p-4 rounded-lg">
                                <p className="font-medium text-finmar-navy">{selectedDoc.filename}</p>
                                <p className="text-sm text-slate-500 mt-1">{(selectedDoc.file_size / 1024).toFixed(1)} KB • {selectedDoc.file_type}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">User</p>
                                    <p className="font-medium">{selectedDoc.user_name}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Business</p>
                                    <p className="font-medium">{selectedDoc.business_name}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Category</p>
                                    <p className="font-medium capitalize">{selectedDoc.category}</p>
                                </div>
                                <div>
                                    <p className="text-slate-500">Uploaded</p>
                                    <p className="font-medium">{new Date(selectedDoc.uploaded_at).toLocaleString()}</p>
                                </div>
                            </div>

                            {selectedDoc.description && (
                                <div>
                                    <p className="text-sm text-slate-500">Description</p>
                                    <p className="text-sm mt-1">{selectedDoc.description}</p>
                                </div>
                            )}

                            <div>
                                <p className="text-sm text-slate-500 mb-2">Update Status</p>
                                <div className="flex flex-wrap gap-2">
                                    {STATUS_OPTIONS.map(status => (
                                        <Button
                                            key={status.value}
                                            variant={selectedDoc.status === status.value ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => handleStatusChange(selectedDoc.document_id, status.value)}
                                            className={selectedDoc.status === status.value ? "bg-finmar-gold text-finmar-navy" : ""}
                                        >
                                            {status.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                            Close
                        </Button>
                        <Button onClick={() => selectedDoc && handleDownload(selectedDoc)} className="bg-finmar-gold hover:bg-finmar-gold/90 text-finmar-navy">
                            <Download className="w-4 h-4 mr-2" />
                            Download
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDocumentsPage;
